// SPDX-License-Identifier: Apache-2.0

// /share — landing page for the share-link feature.
//
// Two distinct contracts touch this URL:
//   1. `/share/:id` (a Cloudflare Worker route, NOT this SolidJS route) —
//      hand-rolled markdown render of an R2-backed shared doc. Runs at the
//      edge, no SPA. See `apps/mcp/workers/share.ts`.
//   2. `/share` (THIS route) — explanatory landing for the SPA. Tells the
//      user what the share-link surface is, how to generate one (via the
//      `share_document` MCP tool from the vault modal), and offers a paste
//      box so they can drop a `/share/:id` URL and jump straight to it.
//
// We render a backend-aware notice when the local MCP isn't reachable: you
// can still VIEW shared docs without it (the Worker handles that), but you
// can't GENERATE new ones without the local server signing a put-URL.

import { Show, createSignal } from "solid-js";
import { isMcpReachable } from "../lib/api";

function extractShareId(input: string): string | null {
  const trimmed = input.trim();
  if (trimmed.length === 0) return null;
  // Accept full URLs, bare paths, or just an id.
  // Match `/share/<id>` anywhere in the string.
  const m = trimmed.match(/share\/([A-Za-z0-9_-]{4,})/);
  if (m && m[1]) return m[1];
  // Fallback — caller pasted just the id.
  if (/^[A-Za-z0-9_-]{4,}$/.test(trimmed)) return trimmed;
  return null;
}

export default function Share() {
  const reachable = isMcpReachable();
  const [pasted, setPasted] = createSignal("");
  const [error, setError] = createSignal<string | null>(null);

  function onOpen(e: Event) {
    e.preventDefault();
    const id = extractShareId(pasted());
    if (!id) {
      setError("Couldn't read a share id from that. Expected something like https://tether.dev/share/abc123 or just abc123.");
      return;
    }
    setError(null);
    // The Worker route lives at the edge; we navigate to it directly so the
    // browser hits the Worker rather than SPA-routing back here.
    window.location.assign(`/share/${id}`);
  }

  return (
    <section style={{ "max-width": "760px", margin: "0 auto" }}>
      <div class="kicker">/share</div>
      <h2 style={{ "margin-top": "8px", "margin-bottom": "16px" }}>
        Shareable doc
      </h2>

      <Show when={!reachable}>
        <div
          class="empty-state warn-tint"
          data-testid="share-no-backend"
          style={{ padding: "20px 24px", "text-align": "left" }}
        >
          <strong>Generating shares needs the local backend.</strong>
          <p style={{ "margin-top": "6px" }}>
            Viewing a shared doc works on any deployment — the{" "}
            <code>/share/:id</code> route is served by a Cloudflare Worker
            against R2 and doesn't need a local server. But{" "}
            <em>creating</em> a share-link signs an R2 PUT URL, which the
            local MCP server (port 5417) does for you.
          </p>
          <p style={{ "margin-top": "6px" }}>
            To create new shares: run <code>bun run dev:mcp</code> in your
            scaffold checkout, then use the share button inside any vault doc.
          </p>
        </div>
      </Show>

      <div class="empty-state" style={{ "text-align": "left", padding: "28px 32px" }}>
        <h2 style={{ "font-size": "20px", "margin-bottom": "12px" }}>
          paste a /share/:id URL
        </h2>
        <p>
          signed, time-bound, R2-backed. Any browser can open one — no login,
          no SPA needed. Generate share-links from the vault by selecting a
          doc and clicking the share icon (uses the <code>share_document</code>{" "}
          MCP tool).
        </p>

        <form
          onSubmit={onOpen}
          style={{
            display: "flex",
            gap: "8px",
            "margin-top": "16px",
            "flex-wrap": "wrap",
          }}
        >
          <input
            type="text"
            value={pasted()}
            onInput={(e) => {
              setPasted(e.currentTarget.value);
              if (error()) setError(null);
            }}
            placeholder="https://tether.dev/share/abc123  —or just—  abc123"
            data-testid="share-paste-input"
            style={{
              flex: "1 1 320px",
              padding: "10px 12px",
              "background": "rgba(0,0,0,0.25)",
              "border": "1px solid var(--border-strong)",
              "border-radius": "6px",
              "color": "var(--text)",
              "font-family": "var(--font-mono)",
              "font-size": "13px",
            }}
          />
          <button
            type="submit"
            data-testid="share-paste-open"
            style={{
              padding: "10px 18px",
              "background": "var(--accent)",
              "border": "none",
              "border-radius": "6px",
              "color": "#0a0a14",
              "font-weight": "600",
              "cursor": "pointer",
            }}
          >
            open
          </button>
        </form>

        <Show when={error()}>
          <p
            class="muted"
            style={{ "margin-top": "10px", color: "var(--rose, #f87171)" }}
            data-testid="share-paste-error"
          >
            {error()}
          </p>
        </Show>

        <p class="muted" style={{ "margin-top": "20px", "font-size": "12px" }}>
          a /share URL is just a doc snapshot uploaded to R2 with a hand-rolled
          edge-rendered viewer. it has no telemetry, no auth, no comments. it's
          read-only by design.
        </p>
      </div>
    </section>
  );
}
